// @systeme: interface
// L'écran À venir : la semaine ou le mois, jour par jour. Et l'accès à « ce qui revient ».
import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { ChevronLeft, ChevronRight, ListChecks, Repeat } from 'lucide-react';
import { Link as LienRouteur } from 'react-router';
import type { PorteeAVenir, VueAVenir } from '@foyer/shared';
import { useActionsElements } from '../composants/ActionsElements';
import { Chargement } from '../composants/Chargement';
import { useMessages } from '../composants/Messages';
import { api } from '../lib/api';
import { fonctionActive } from '../lib/fonctionnalites';
import { useSessionConnectee } from '../session/SessionContext';
import { formaterJourLong } from '../lib/dates';
import { tailles } from '../theme/theme';
import { premiereMajuscule } from './Aujourdhui';

export function AVenir() {
  const session = useSessionConnectee();
  const { direErreur } = useMessages();
  const [portee, setPortee] = useState<PorteeAVenir>('semaine');
  const [depuis, setDepuis] = useState<string | undefined>();
  const [vue, setVue] = useState<VueAVenir | undefined>();
  const [aujourdhui, setAujourdhui] = useState<{ jour: string; jourDemain: string } | undefined>();

  const charger = useCallback(async () => {
    try {
      setVue(await api.elements.aVenir(portee, depuis));
    } catch (e) {
      direErreur(e);
    }
  }, [portee, depuis, direErreur]);

  useEffect(() => {
    api.temps.aujourdhui().then(setAujourdhui).catch(direErreur);
  }, [direErreur]);

  useEffect(() => {
    api.elements.aVenir(portee, depuis).then(setVue).catch(direErreur);
  }, [portee, depuis, direErreur]);

  const { ligne, feuilles } = useActionsElements({
    jourAujourdhui: aujourdhui?.jour ?? '',
    jourDemain: aujourdhui?.jourDemain ?? '',
    recharger: charger,
    ecran: '/a-venir',
  });

  if (!vue) return <Chargement />;

  // Le mois ne montre que les jours où il se passe quelque chose ; la semaine montre tout.
  const jours =
    portee === 'semaine' ? vue.jours : vue.jours.filter((j) => j.elements.length > 0 || j.ferie);
  const nombreJours = portee === 'semaine' ? 7 : 31;
  // Les jours suivants et précédents sont donnés par le serveur : ce sont ses « fin » et « debut ».
  const suivant = vue.jours[nombreJours - 1] ? vue.fin : undefined;

  return (
    <Box sx={{ px: `${tailles.marge}px`, pt: 3 }}>
      <Typography variant="h1" component="h1" sx={{ mb: 1 }}>
        À venir
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Button
          component={LienRouteur}
          to="/a-venir/recurrences"
          startIcon={<Repeat size={18} />}
          color="inherit"
          sx={{ px: 1 }}
        >
          Ce qui revient
        </Button>
        {fonctionActive(session, 'preparations') && (
          <Button
            component={LienRouteur}
            to="/a-venir/preparations"
            startIcon={<ListChecks size={18} />}
            color="inherit"
            sx={{ px: 1 }}
          >
            Ce que je prépare
          </Button>
        )}
      </Stack>

      <ToggleButtonGroup
        exclusive
        value={portee}
        onChange={(_e, v: PorteeAVenir | null) => v && (setPortee(v), setDepuis(undefined))}
        fullWidth
        sx={{ mb: 2 }}
      >
        <ToggleButton value="semaine">La semaine</ToggleButton>
        <ToggleButton value="mois">Le mois</ToggleButton>
      </ToggleButtonGroup>

      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
        <Button
          color="inherit"
          startIcon={<ChevronLeft size={18} />}
          disabled={depuis === undefined}
          onClick={() => setDepuis(undefined)}
        >
          Aujourd’hui
        </Button>
        <Button
          color="inherit"
          endIcon={<ChevronRight size={18} />}
          onClick={() => suivant && setDepuis(suivant)}
        >
          Plus tard
        </Button>
      </Stack>

      {jours.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          Rien de prévu sur cette période.
        </Typography>
      )}

      {jours.map((j) => (
        <Box key={j.jour} sx={{ mb: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="h3" component="h2">
              {premiereMajuscule(formaterJourLong(j.jour))}
            </Typography>
            {j.ferie && (
              <Chip
                label={j.ferie}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 26 }}
              />
            )}
          </Stack>
          {j.elements.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
              —
            </Typography>
          ) : (
            <List disablePadding aria-label={j.jour}>
              {j.elements.map(ligne)}
            </List>
          )}
        </Box>
      ))}

      {feuilles}
    </Box>
  );
}
